import React, { FC, useEffect, useMemo, useRef, useState } from 'react';
import { connect } from 'react-redux';
import DeliverySLA from './DeliverySLA';
import B2cSellerModal, { B2cSellerModalProduct } from './B2cSellerModal';
import { mapSlaOptions } from './mapSlaResponse';
import {
  selectHasMoreSellers,
  selectIsSlaFetching,
  selectSlaError,
  selectSlaOptions
} from './selectSla';
import { showModal } from '../../../actions/shared/modal';
import { selectSlaFromViewOption } from '../../../edd/actions';
import { ModalTypes } from '../../../constants/index.js';
import { getB2cSellerConfig } from '../../../refresh/selectors/featureFlagsSelector';
import {
  SLA_SURFACE,
  SlaSurface,
  getSlaSurfaceScreenNames
} from '../../../refresh/molecules/DeliverySLA/constants';
import useDeliveryDetailsViewed from '../../../refresh/molecules/DeliverySLA/useDeliveryDetailsViewed';
import {
  pushDeliveryReturnDetailsViewedB2c,
  pushOtherSellersAvailableClickedB2c
} from '../../../modules/ga/GTMdatalayer';
import { PriceTypeTags, RawSlaOption, SlaCodeLabels, SlaOption } from './types';

const DEFAULT_OTHER_SELLERS_LABEL = 'Other sellers available';
import styles from './index.pcss';

interface StateProps {
  activePincode?: string;
  footerNote?: string;
  /** Badge copy per priceType — b2cSellerConfig.priceTypeTags. */
  priceTypeTags?: PriceTypeTags;
  /** SLA-code → copy map — b2cSellerConfig.slaTimeMessage. */
  slaCodeLabels?: SlaCodeLabels;
  otherSellersLabel?: string;
  /** scopedView options for the selected size, as stored by fetchDeliveryDetailsV2. */
  storeSlaOptions?: RawSlaOption[];
  isFetching?: boolean;
  error?: boolean;
  /** `hasMoreSellers` on the scopedView response — gates the seller-list link. */
  hasMoreSellers?: boolean;
  /** Seller-list modal on screen — this card holds its last response until it closes. */
  sellerModalOpen?: boolean;
  /** For "Delivery And Return Details Viewed" — products[0] and size_selected. */
  productData?: any;
  selectedSizeCode?: string;
  selectedSizeName?: string;
  /** All SKUs cached for the pincode, for is_multi_seller_enabled_size. */
  slaDetailsBySku?: Record<string, any> | null;
}

interface DispatchProps {
  openPincodeModal: () => void;
  openSellerListModal: () => void;
  /**
   * Applies the picked option's price to the product slice and, under B2C, puts
   * the CTA back to "Add to Bag" — the bag line is tied to the option that was
   * selected when it was added.
   */
  selectSlaOption: (option: SlaOption) => void;
}

interface OwnProps {
  /** luxe theme, forwarded from the desktop PDP (newDesignLuxe) */
  luxe?: boolean;
  /**
   * SKU whose scopedView response to render. Defaults to the PDP's selected
   * size; hosts that keep their own size state (the cart / wishlist Size-Quantity
   * popup, which works off `cart.size`) pass it.
   */
  skuId?: string;
  /**
   * Fired when the user picks another seller card. Hosts that commit their own
   * line (cart / wishlist popup) take the pick from here — the PDP has nothing
   * to commit, so it omits this and the pick goes to the product slice instead.
   */
  onSelectOption?: (option: SlaOption) => void;
  /**
   * Hide the pincode / Change Pincode row. The Size-Quantity popup supplies its
   * own "Delivery Detail" heading and has no pincode row in the design.
   */
  hideHeader?: boolean;
  /** Heading rendered above the cards, when the host wants one inside the card. */
  title?: React.ReactNode;
  /** Seller row copy, when the host names a different seller than the PDP. */
  sellerName?: string;
  /** Raw options to render, when the host has its own card set. */
  slaOptions?: RawSlaOption[];
  /**
   * Every seller for this size, for the seller-list modal. Unlike `slaOptions`
   * this list is not filtered by `defaultDisplay` — showing all of them is the
   * point of that modal.
   */
  allSellerOptions?: RawSlaOption[];
  /**
   * From the API's `hasMoreSellers`. Only then is there a list worth opening, so
   * "Other sellers available" is gated on it. Left undefined by hosts that have
   * no API answer yet, which keeps the link as it was.
   */
  hasMoreSellers?: boolean;
  /**
   * Product the seller-list modal describes. Omitted on the PDP, where the modal
   * reads the `product` slice itself.
   */
  modalProduct?: B2cSellerModalProduct | null;
  /** Size picked inside the seller-list modal. */
  onSelectModalSize?: (sizeCode: string, sizeLabel: string) => void;
  /** "Add to Bag" inside the seller-list modal. */
  onAddToBag?: (sizeCode: string, selectedPriceType: string) => void;
  /**
   * "Other sellers available", when the host has to do something before the
   * modal opens. The wishlist closes its Size/Quantity popup first, so it also
   * has to host the modal itself — see `renderSellerModal`.
   */
  onOtherSellersClick?: () => void;
  /**
   * Render the seller-list modal here. Off for hosts that mount it themselves,
   * outside whatever this card sits in: `Modal` returns null while hidden, so a
   * modal nested inside a closing popup unmounts with it and never shows.
   */
  renderSellerModal?: boolean;
  /**
   * Which screen this card is mounted on, for analytics. The cart / wishlist
   * Size-Quantity popup passes its own, so "Delivery And Return Details Viewed"
   * reports the bag / wishlist screen instead of the PDP.
   */
  surface?: SlaSurface;
  /** Already-mapped analytics product item, from hosts that own their entity. */
  productItem?: Record<string, unknown> | null;
  /** Size label for `size_selected`, from hosts with their own size state. */
  sizeSelectedName?: string;
}

type Props = StateProps & DispatchProps & OwnProps;

/**
 * Container for the B2C seller Delivery SLA card (`b2cSellerConfig.isEnabled`).
 *
 * Reuses the multiseller Delivery SLA UI as-is; only the data source differs —
 * static mock options run through the same `mapSlaOptions` mapper until the B2C
 * SLA API is integrated. Mounted only when the flag is on, so the flag being
 * off leaves its hosts exactly as they are today.
 *
 * Hosted by the desktop PDP (which supplies nothing beyond `luxe` and gets the
 * pincode header) and by the desktop cart / wishlist Size-Quantity popup, which
 * hides that header and hands over its own card set, seller name and product for
 * the seller-list modal.
 */
const B2cSellerDeliverySLA: FC<Props> = ({
  activePincode,
  footerNote,
  priceTypeTags,
  slaCodeLabels,
  otherSellersLabel,
  storeSlaOptions,
  isFetching,
  error,
  hasMoreSellers,
  sellerModalOpen,
  openPincodeModal,
  openSellerListModal,
  selectSlaOption,
  luxe = false,
  hideHeader = false,
  title = null,
  sellerName,
  slaOptions,
  onSelectOption,
  allSellerOptions,
  modalProduct = null,
  onSelectModalSize,
  onAddToBag,
  onOtherSellersClick,
  renderSellerModal = true,
  surface = SLA_SURFACE.PDP,
  productItem = null,
  sizeSelectedName,
  productData,
  selectedSizeCode,
  selectedSizeName,
  slaDetailsBySku
}) => {
  // The PDP hands over nothing and reads the scopedView response from the store;
  // the cart / wishlist popups own their card set and pass it in.
  const hostOwnsOptions = Array.isArray(slaOptions);

  /**
   * Snapshot of everything the card shows, taken when the seller-list modal
   * opens and held until it closes.
   *
   * The modal changes the page under itself — size selection moves
   * `selectedSizeCode` on (with no scopedView call for the card, by design) and
   * the picked option / pincode can move too. None of that should reach the card
   * behind the modal: it re-scopes on close, and that response is the one worth
   * rendering. Frozen wholesale rather than per field so the cards, seller row
   * and "Other sellers" link can never disagree with each other.
   */
  const frozenRef = useRef<{
    options?: RawSlaOption[];
    hasMoreSellers?: boolean;
    activePincode?: string;
  } | null>(null);
  if (!hostOwnsOptions) {
    if (sellerModalOpen) {
      if (!frozenRef.current) {
        frozenRef.current = { options: storeSlaOptions, hasMoreSellers, activePincode };
      }
    } else if (frozenRef.current) {
      frozenRef.current = null;
    }
  }
  const frozen = hostOwnsOptions ? null : frozenRef.current;

  const rawOptions = hostOwnsOptions ? slaOptions : frozen?.options ?? storeSlaOptions;
  const shownHasMoreSellers = frozen ? frozen.hasMoreSellers : hasMoreSellers;
  const shownPincode = frozen ? frozen.activePincode : activePincode;

  const viewModel = useMemo(
    () => mapSlaOptions(rawOptions, priceTypeTags, { dedupeByPriceType: false }),
    [priceTypeTags, rawOptions]
  );

  /**
   * Seller shown under the cards follows the card the user is actually on.
   *
   * Held here rather than read back off the view model: that carries the API's
   * `isSelected`, which does not move when the user picks another card (the
   * selection lives inside DeliverySLA). Reset whenever a new view model
   * arrives, so a stale name cannot outlive the options it came from.
   */
  const [pickedSellerName, setPickedSellerName] = useState('');
  useEffect(() => {
    // Functional form so an unchanged name bails out of the render entirely —
    // a `viewModel` whose identity churns would otherwise re-render on every
    // effect run and trip React's update-depth limit.
    setPickedSellerName(previous => (previous ? '' : previous));
  }, [viewModel]);

  // Host-supplied name wins, then the user's pick, then whatever the API marked.
  const selectedSellerName =
    sellerName ||
    pickedSellerName ||
    (viewModel?.options || []).find(option => option.isSelected)?.sellerName ||
    viewModel?.options?.[0]?.sellerName ||
    '';

  // The link only makes sense when sellers exist beyond the cards on screen.
  // Cart (host-owned deliveryChoices) gates on that payload's `hasMoreSellers`,
  // same as msite. PDP / wishlist keep the scopedView flag.
  const showOtherSellers = hostOwnsOptions
    ? Boolean(hasMoreSellers)
    : Boolean(shownHasMoreSellers);

  // A host that commits its own line takes the pick itself; only the PDP's own
  // cards report back to the product slice.
  const commitPick = onSelectOption || (hostOwnsOptions ? undefined : selectSlaOption);
  const deferredPickRef = useRef<number | null>(null);
  useEffect(
    () => () => {
      if (deferredPickRef.current) window.clearTimeout(deferredPickRef.current);
    },
    []
  );

  /**
   * Applying the pick moves the product price, which re-renders a large part of
   * the page — measured at ~640ms on the PDP. Run inline that blocks the paint,
   * so the radio only turns on once it finishes and the tap reads as ignored.
   *
   * Handing it to the next task lets the selection (already optimistic in
   * useDeliverySLA) paint first. Nothing reads the result inside that gap — the
   * gap is one task, and the seller row below is updated synchronously above.
   */
  const handleSelectOption = (option: SlaOption): void => {
    // Paints with the radio — this is the card the user just picked.
    setPickedSellerName(option?.sellerName || '');
    if (deferredPickRef.current) window.clearTimeout(deferredPickRef.current);
    deferredPickRef.current = window.setTimeout(() => {
      deferredPickRef.current = null;
      commitPick?.(option);
    }, 0);
  };

  /**
   * "Delivery And Return Details Viewed", B2C cohort. `estimatedDeliveryDate`
   * renders this card instead of `DeliverySLAContainer` when the flag is on, so
   * the container's emitter never runs and the event is pushed from here.
   */
  const { ref: viewedRef } = useDeliveryDetailsViewed({
    // The seller-list modal covers the card; hold the event until it closes.
    enabled: !sellerModalOpen,
    // A host-owned card set (the popup's deliveryChoices response) has no fetch
    // of its own here, so the store's fetching flag says nothing about it.
    ready: (hostOwnsOptions || !isFetching) && Array.isArray(rawOptions) && rawOptions.length > 0,
    resetKeys: [activePincode, selectedSizeCode],
    payload: () =>
      pushDeliveryReturnDetailsViewedB2c({
        platform: 'desktop',
        screenNames: getSlaSurfaceScreenNames(surface, { isDesktop: true }),
        pincode: activePincode,
        sizeSelected: sizeSelectedName || selectedSizeName,
        sizeSelectedFlag: Boolean(selectedSizeCode),
        slaOptions: rawOptions,
        slaDetailsBySku,
        deliveryInfoText: footerNote,
        // What the card actually offers — a host-owned card set always carries
        // its full seller list, so the link shows and the event has to agree.
        otherSellersAvailable: showOtherSellers,
        productItem,
        // PDP only: in the cart / wishlist popup the product slice holds
        // whatever PDP was last visited, not the line on screen — that host
        // passes `productItem` instead.
        productData: surface === SLA_SURFACE.PDP ? productData : null
      })
  });

  /**
   * "Other Seller Available Clicked", then whatever opening the list means for
   * this host — the popup hosts pass their own opener, and the event has to
   * report for them too.
   */
  const handleOtherSellersClick = (): void => {
    pushOtherSellersAvailableClickedB2c({
      screenNames: getSlaSurfaceScreenNames(surface, { isDesktop: true }),
      slaOptions: rawOptions,
      sizeSelected: sizeSelectedName || selectedSizeName,
      productItem,
      // Same split as the viewed event above.
      productData: surface === SLA_SURFACE.PDP ? productData : null
    });
    (onOtherSellersClick || openSellerListModal)();
  };

  return (
    <div ref={viewedRef}>
      <DeliverySLA
        viewModel={viewModel}
        // While the modal holds the snapshot there is nothing in flight for this
        // card, so neither state applies.
        loading={hostOwnsOptions || frozen ? false : isFetching}
        error={hostOwnsOptions || frozen ? false : error}
        activePincode={shownPincode}
        // The popup has no pincode row, so the change-pincode flow goes with it.
        onChangePincode={hideHeader ? undefined : openPincodeModal}
        hideHeader={hideHeader}
        title={title}
        footerNote={footerNote}
        onSelectOption={handleSelectOption}
        sellerName={selectedSellerName}
        otherSellersLabel={showOtherSellers ? otherSellersLabel : ''}
        onOtherSellersClick={handleOtherSellersClick}
        slaCodeLabels={slaCodeLabels}
        className={styles['sla-b2c']}
        luxe={luxe}
      />
      {/* Renders nothing until the modal is shown (Modal reads its own state). */}
      {renderSellerModal && (
        <B2cSellerModal
          luxe={luxe}
          slaOptions={allSellerOptions}
          product={modalProduct}
          onSelectSize={onSelectModalSize}
          onAddToBag={onAddToBag}
          surface={surface}
        />
      )}
    </div>
  );
};

const mapStateToProps = (state: any, ownProps: OwnProps): StateProps => {
  const b2cSellerConfig = getB2cSellerConfig(state) || {};
  const activePincode = state?.edd?.activePincode;
  // The popup hosts keep their own size state, so the sku they render is theirs;
  // the PDP has none to pass and rides its own product slice.
  const selectedSizeCode = ownProps?.skuId || state?.product?.selectedSizeCode || '';
  return {
    activePincode,
    footerNote: b2cSellerConfig?.deliverySlaNote,
    // B2C card badges come from its own flag, never multiSellerSlaConfig.
    priceTypeTags: b2cSellerConfig?.priceTypeTags,
    slaCodeLabels: b2cSellerConfig?.slaTimeMessage,
    otherSellersLabel: b2cSellerConfig?.otherSellersLabel || DEFAULT_OTHER_SELLERS_LABEL,
    storeSlaOptions: selectSlaOptions(state, activePincode, selectedSizeCode),
    isFetching: selectIsSlaFetching(state, activePincode, selectedSizeCode),
    error: selectSlaError(state, activePincode, selectedSizeCode),
    // Cart passes deliveryChoices.hasMoreSellers (including false). Do not let
    // the scopedView cache overwrite it — that is what made the cart link show
    // for sizes with no extra sellers.
    hasMoreSellers:
      ownProps.hasMoreSellers !== undefined
        ? Boolean(ownProps.hasMoreSellers)
        : selectHasMoreSellers(state, activePincode, selectedSizeCode),
    sellerModalOpen: Boolean(state?.modals?.[ModalTypes.B2C_SELLER_LIST]),
    // Analytics inputs. `selectedSizeName` is what the multiseller container is
    // handed for `size_selected`, so it is read off the same slice here.
    productData: state?.product?.productDetails,
    selectedSizeCode,
    selectedSizeName: state?.product?.selectedSizeName || '',
    slaDetailsBySku: (activePincode && state?.edd?.slaDetails?.[activePincode]) || null
  };
};

const mapDispatchToProps = (dispatch: any): DispatchProps => ({
  openPincodeModal: () => dispatch(showModal(ModalTypes.EDD_PINCODE)),
  openSellerListModal: () => dispatch(showModal(ModalTypes.B2C_SELLER_LIST)),
  selectSlaOption: (option: SlaOption) => dispatch(selectSlaFromViewOption(option))
});

export default connect(mapStateToProps, mapDispatchToProps)(B2cSellerDeliverySLA);
